import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { SimplePDFViewer } from '@/components/SimplePDFViewer';
import { TipTapContent } from '@/components/TipTapContent';
import { toast } from 'sonner';
import {
  Download,
  Maximize,
  Minimize,
  ExternalLink,
  Loader2
} from 'lucide-react';

export function DocumentPostViewer({
  post,
  formatFileSize,
  getFileIcon,
  getFileIconColor
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const containerRef = useRef(null);

  const isPdf = post.document_type === 'application/pdf';

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  };

  const handleDownload = async () => {
    try {
      toast.info('Starting download...');
      const response = await fetch(post.document_url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = post.document_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      window.open(post.document_url, '_blank');
    }
  };

  const handleOpenInNewTab = () => {
    window.open(post.document_url, '_blank');
  };

  const IconComponent = getFileIcon(post.document_type);
  const iconColor = getFileIconColor(post.document_type);

  // For PDFs, use our custom viewer
  if (isPdf) {
    return (
      <div className='space-y-4'>
        <div className='border rounded-lg overflow-hidden h-[80vh]'>
          <SimplePDFViewer url={post.document_url} className="h-full" />
        </div>

        {/* Description */}
        {post.content && (
          <div className='mt-4'>
            <TipTapContent
              content={post.content}
              className='text-sm text-muted-foreground'
            />
          </div>
        )}
      </div>
    );
  }

  // For Office documents, use Google Docs Viewer with fullscreen support
  return (
    <div className='space-y-4'>
      <div
        ref={containerRef}
        className={`border rounded-lg overflow-hidden bg-muted/30 ${
          isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''
        }`}
      >
        {/* Toolbar */}
        <div className='flex items-center justify-between p-3 border-b bg-background sticky top-0 z-10'>
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            {IconComponent && <IconComponent className={`h-4 w-4 ${iconColor || ''}`} />}
            <span className='font-medium'>{post.document_name || 'Document'}</span>
            {post.document_size && (
              <>
                <span>•</span>
                <span>{formatFileSize(post.document_size)}</span>
              </>
            )}
          </div>

          {/* Actions */}
          <div className='flex items-center gap-1'>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleOpenInNewTab}
              className='h-8 px-2'
              title='Open in new tab'
            >
              <ExternalLink className='h-4 w-4' />
            </Button>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleFullscreen}
              className='h-8 px-2'
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                <Minimize className='h-4 w-4' />
              ) : (
                <Maximize className='h-4 w-4' />
              )}
            </Button>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleDownload}
              className='h-8 px-2'
              title='Download'
            >
              <Download className='h-4 w-4' />
            </Button>
          </div>
        </div>

        {/* Document Viewer */}
        <div className={`relative ${isFullscreen ? 'h-[calc(100vh-57px)]' : 'h-[80vh]'}`}>
          {iframeLoading && (
            <div className='absolute inset-0 flex flex-col items-center justify-center bg-muted/50'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
              <p className='mt-3 text-sm text-muted-foreground'>Loading document...</p>
            </div>
          )}
          <iframe
            src={`https://docs.google.com/gview?url=${encodeURIComponent(post.document_url)}&embedded=true`}
            className='w-full h-full border-0 bg-white'
            title={post.document_name}
            onLoad={() => setIframeLoading(false)}
          />
        </div>
      </div>

      {/* Description */}
      {post.content && (
        <div className='mt-4'>
          <TipTapContent
            content={post.content}
            className='text-sm text-muted-foreground'
          />
        </div>
      )}
    </div>
  );
}
